import type { As, PropsOf, SystemStyleObject } from '@chakra-ui/react';
import { chakra, useStyleConfig } from '@chakra-ui/react';
import React from 'react';

import { withChakraAs } from '../../utils/withChakraAs';

type TextThemeProps =
  | {
      variant?: never;

      /**
       * The size of the text.
       *
       * @default base
       */
      size?: 'base' | 'sm';
    }
  | {
      /**
       * The rendered style of the text.
       *
       * @default body
       */
      variant: 'body' | 'bodySb' | 'codeSb';

      /**
       * The size of the text.
       *
       * @default base
       */
      size?: 'base' | 'sm';
    }
  | {
      /**
       * The rendered style of the text.
       *
       * @default body
       */
      variant: 'code';

      /**
       * The size of the text.
       *
       * @default base
       */
      size?: 'base' | 'sm' | '2xl';
    }
  | {
      /**
       * The rendered style of the text.
       *
       * @default body
       */
      variant: 'label';
      size?: never;
    }
  | {
      /**
       * The rendered style of the text.
       *
       * @default body
       */
      variant: 'subtitle';
      size?: never;
    };

export type TextProps<Component extends As = 'p'> = PropsOf<Component> &
  TextThemeProps & {
    /**
     * Additional style config needed for component that wraps `Text`. This
     * should only be used internally by other Orbit components. Please use
     * `className` for all styling.
     *
     * @internal
     */
    __orbitStyleConfig?: SystemStyleObject;
  };

export const Text = withChakraAs<TextProps, 'span'>(
  ({ variant = 'body', size = 'base', __orbitStyleConfig, ...props }) => {
    const styles = useStyleConfig('Text', { variant, size });

    return <chakra.p {...props} __css={{ ...styles, ...__orbitStyleConfig }} />;
  },
);
